<template>
  <Loader
    :value="promises.fetchOne"
    :message="$metaInfo.title"
    :class="['purchase-mutate', { 'purchase-mutate--signed': isSigned }]"
  >
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs" />

      <Card tag="form" @submit.prevent="() => submit()" class="purchase-mutate__form">
        <template #default>
          <div class="purchase-mutate__inputs">
            <TemplateUpload
              v-model="data.template.document"
              data-field="document"
              :label="labels.purchaseContract"
              :upload-options="{ acl: 'private' }"
              required
            >
              <template #below>
                <CheckboxComponent v-model="isSigned" :label="$t('signedContractSkipSigningMessage')" />
              </template>
            </TemplateUpload>

            <FileComponent
              data-field="attachment"
              v-if="!isSigned"
              v-model="data.template.attachments"
              :label="`${$tc('attachment', 2)} (${labels.purchaseContract})`"
              accept=".pdf"
              :upload-options="{ acl: 'private' }"
              multiple
            >
              <template #below>
                <Alert class="mt-1">
                  <div>{{ $t('attachmentsSigningUploadWarning') }}</div>
                </Alert>
              </template>
            </FileComponent>
          </div>
          <CustomFields
            v-if="customFieldSchemas.length"
            v-model="data.data.customFields"
            :schema="customFieldSchemas"
            placement="purchase:prefill"
            :disabledFields="$path('project.metadata.disabledFields', item)"
          />
          <hr class="mt-4 mb-3" />
          <div>
            <h2>{{ $tc('buyer') }}</h2>
            <Applicant v-model="data.data" />
          </div>
        </template>
        <template #footer>
          <template v-if="!isSigned">
            <ButtonComponent
              :label="$t('save')"
              theme="primary"
              type="submit"
              :disabled="!validator.passes"
              :icon="['fal', 'arrow-to-bottom']"
              :promise="promises.mutate"
            />
            <ButtonComponent
              :label="$t('preview')"
              :icon="['fal', 'eye']"
              :tag="isReady ? 'a' : 'button'"
              :disabled="!isReady"
              :href="`/api/contract/${$path('contract.purchase.id', item)}/preview`"
              target="_blank"
              style="margin-left: auto"
            />
            <ButtonComponent
              :label="$t('sendToSigning')"
              theme="success"
              type="button"
              :promise="signPromise"
              confirm
              @click="sendToSigning"
              :disabled="!isReady"
              :icon="['fal', 'file-contract']"
            />
          </template>
          <ButtonComponent
            v-else
            :label="$t('complete')"
            theme="success"
            type="button"
            :promise="signPromise"
            confirm
            @click="saveSignedDocument"
            :disabled="!validator.passes"
            :icon="['fal', 'file-contract']"
            style="margin-left: auto"
          />
        </template>
      </Card>
    </div>
  </Loader>
</template>

<script>
import Validator from '@/mixins/validator'
import { mapState, mapActions } from 'vuex'
import Model from '@/models/Purchase/Mutate'
import { mergeDeepRight } from 'ramda'
import { Clone, PrefixObjectKeys } from '@/utils'

import HeaderComponent from '@/components/Header'
import { InputComponent } from 'vue-elder-input'
import { FileComponent } from '@kvass/file'
import { ChoiceComponent } from 'vue-elder-choice'
import { AlertComponent as Alert } from 'vue-elder-alert'
import Applicant, { rules as ApplicantRules, labels as ApplicantLabels } from '@/components/Contract/Applicant'
import Card from '@/components/Card'
import { CheckboxComponent } from 'vue-elder-checkbox'
import CustomFields from '@kvass/custom-fields'
import TemplateUpload from '@/components/Contract/TemplateUpload.vue'

export default {
  mixins: [Validator],
  props: {
    id: String,
  },
  watch: {
    item: {
      handler() {
        if (
          !this.item ||
          !this.item.contract ||
          !this.item.contract.purchase ||
          this.item.contract.purchase.status !== 'draft'
        )
          return
        this.data = Model(this.item.contract.purchase)
        if (this.item.project.customFields)
          this.customFields = this.item.project.customFields.map(e => ({
            ...e,
            data: e.data || this.$path('contract.purchase.data.customFields.' + e.key, this.item),
          }))
      },
      immediate: true,
      deep: true,
    },
  },
  data() {
    return {
      data: Model(),
      customFields: null,
      signPromise: null,
      validator: {
        rules: 'rulesComp',
        labels: 'labels',
        data: 'data',
      },
      isSigned: false,
    }
  },
  computed: {
    ...mapState('Contract', ['item', 'promises']),
    labels() {
      return {
        purchaseContract: this.$t('purchaseContract'),
        ...PrefixObjectKeys('data.', ApplicantLabels),
      }
    },
    customFieldSchemas() {
      if (!this.item.project) return []
      return this.item.project.customFieldsConfig.map(c => c.schema).flat()
    },
    breadcrumbs() {
      return [
        { title: this.$tc('project', 2), action: { name: 'projects' } },
        {
          title: this.$path('item.project.name'),
          action: {
            name: 'project-dashboard',
            params: { id: this.$path('item.project.id') },
          },
        },
        {
          title: this.item.name,
        },
        { title: this.$metaInfo.title },
      ]
    },
    rulesComp() {
      let base = {
        data: {
          ...ApplicantRules,
        },
      }

      if (this.isSigned) return { ...base, document: 'required' }
      else return { ...base, 'template.document': 'required' }
    },
    isReady() {
      return this.validator.passes && this.$path('item.contract.purchase.template.document')
    },
  },
  methods: {
    ...mapActions('Contract', ['fetch', 'mutate']),

    prepareData() {
      let data = Clone(this.data)
      if (!data.data.hasCoApplicant) data.data.coApplicant = null

      return data
    },
    submit(data) {
      if (!data) data = this.prepareData()
      return this.mutate([
        data,
        {
          mutation: 'PurchaseUpdate',
          strategy: (item, payload) => {
            return mergeDeepRight(item, { contract: { purchase: payload } })
          },
        },
      ])
    },
    saveSignedDocument() {
      let data = this.prepareData()
      data.status = 'accepted'
      this.signPromise = this.submit(data).then(() => {
        this.$router.push({ name: 'project-dashboard', params: { id: this.item.project.id } })
      })
    },
    sendToSigning() {
      let data = this.prepareData()
      data.status = 'unsigned'
      this.signPromise = this.submit(data).then(() => {
        this.$router.push({ name: 'project-dashboard', params: { id: this.item.project.id } })
      })
    },
  },
  created() {
    return this.fetch([
      { id: this.id },
      {
        query: 'ResidentialPurchase',
      },
    ]).then(() => {
      if (
        !this.item ||
        !this.item.contract ||
        !this.item.contract.purchase ||
        this.item.contract.purchase.status !== 'draft'
      )
        return this.$router.push({ name: 'project-dashboard', params: { id: this.item.project.id } })
    })
  },
  metaInfo() {
    return {
      title: this.$t('preparePurchaseContract'),
    }
  },
  components: {
    InputComponent,
    HeaderComponent,
    Card,
    FileComponent,
    ChoiceComponent,
    Applicant,
    Alert,
    CheckboxComponent,
    CustomFields,
    TemplateUpload,
  },
}
</script>

<style lang="scss">
.purchase-mutate {
  background-color: $background-grey;

  &__form {
    margin: 0 auto;
    max-width: 1100px;
  }

  &__inputs {
    display: grid;
    align-items: center;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: $form-gap-row / 2 $form-gap-column;
    align-items: flex-start;
  }

  &--signed [data-field='document'] {
    grid-column-end: span 2;
  }

  [data-field='references'] {
    .elder-input__element {
      padding: 0;
    }
    .select-checklist__items {
      padding: 1.1em;
      max-height: 250px;
      overflow-y: auto;

      @include scrollShadow();
    }
  }

  .custom-fields {
    grid-gap: 1rem 2rem;
  }
}
</style>
